import { ActivatedRoute, Router } from '@angular/router'
import { Component, OnInit } from '@angular/core'
import { Document } from 'app/models/document'
import { File } from 'app/models/file'
import { DocumentService } from 'app/services/document.service'
import { FilesService } from 'app/services/files.service'
import { TopicService } from 'app/services/topic.service'
import { StoryboardService } from 'app/services/storyboard.service'
import { Source } from 'app/models/source.enum'
import { FileType } from 'app/models/file-type.enum'

@Component({
  selector: 'app-file',
  templateUrl: './file.component.html',
  styleUrls: ['./file.component.scss']
})
export class FileComponent implements OnInit {
  file: Document | File
  parentType: string = ''
  parentId: string = ''

  constructor(
    private documentService: DocumentService,
    private filesService: FilesService,
    private topicService: TopicService,
    private storyboardService: StoryboardService,
    private activatedRoute: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit() {
    this.activatedRoute.params.subscribe((params: any) => {
      this.getFile(params.id)
    })
  }

  getFile(id): void {
    // Check if we're coming from a parent context (topic or storyboard)
    const urlSegments = this.router.url.split('/')
    const topicIndex = urlSegments.indexOf('topic')
    const storyboardIndex = urlSegments.indexOf('storyboard')

    // If we have a topic or storyboard in the URL, load from parent context
    if (topicIndex !== -1 && urlSegments[topicIndex + 1]) {
      const topicId = urlSegments[topicIndex + 1]
      this.parentType = 'topic'
      this.parentId = topicId
      this.topicService.getById(topicId).subscribe(
        topic => {
          const file = topic.files?.find(f => f.id === id)
          if (file) {
            file.containerId = topic.id
            // Ensure fileUrl is set for video/image files from contentId if missing
            this.ensureFileUrl(file)
            this.file = file as File
          } else {
            // Fallback to standalone file loading
            this.loadStandaloneFile(id)
          }
        },
        error => {
          // Fallback to standalone file loading
          this.loadStandaloneFile(id)
        }
      )
    } else if (storyboardIndex !== -1 && urlSegments[storyboardIndex + 1]) {
      const storyboardId = urlSegments[storyboardIndex + 1]
      this.parentType = 'storyboard'
      this.parentId = storyboardId
      this.storyboardService.getById(storyboardId).subscribe(
        storyboard => {
          const file = storyboard.files?.find(f => f.id === id)
          if (file) {
            file.containerId = storyboard.id
            this.file = file
          } else {
            // Fallback to standalone file loading
            this.loadStandaloneFile(id)
          }
        },
        error => {
          // Fallback to standalone file loading
          this.loadStandaloneFile(id)
        }
      )
    } else {
      // No parent context, load standalone
      this.loadStandaloneFile(id)
    }
  }

  private loadStandaloneFile(id: string): void {
    // Try to get from assets first (for competition files like videos).
    // NOTE: op=assets returns HTTP 200 with {Items:[]} when the file is in the
    // DOCUMENT_TABLE instead of ASSETS_TABLE. We must treat an empty/invalid
    // response as a miss and fall through to the document service.
    this.filesService.get(id).subscribe(
      (file: any) => {
        // Validate the response is an actual file record, not an empty API envelope
        if (!file || !file.contentType || file.Items !== undefined) {
          this.loadFromDocuments(id)
          return
        }
        this.ensureFileUrl(file)
        this.file = file
      },
      _error => {
        this.loadFromDocuments(id)
      }
    )
  }

  private loadFromDocuments(id: string): void {
    this.documentService.getById(id).subscribe(
      file => {
        file.source = Source.Document
        if (file.contentType && typeof file.contentType === 'string') {
          file.contentType = (file.contentType as string).toUpperCase() as any;
          if ((file.contentType as string) === 'PDF' || (file.contentType as string) === 'APPLICATION/PDF' || (file.contentType as string) === 'APPLICATION/X-PDF') {
            file.contentType = FileType.Doc;
          }
        }
        this.file = file
      },
      docError => {
        console.error('File not found in either assets or documents:', id, docError)
      }
    )
  }

  private ensureFileUrl(file: any): void {
    // Normalize contentType and source to uppercase for consistency
    if (file.contentType && typeof file.contentType === 'string') {
      file.contentType = file.contentType.toUpperCase()
    }
    if (file.source && typeof file.source === 'string') {
      file.source = file.source.toUpperCase()
    }

    // If fileUrl is missing but contentId exists, fetch it from the backend
    if (!file.fileUrl && file.contentId) {
      // The backend should provide a way to get the file URL from contentId
      // For now, we'll request the file details which should include the fileUrl
      this.filesService.get(file.id).subscribe(
        fullFile => {
          if (fullFile.fileUrl) {
            file.fileUrl = fullFile.fileUrl
          }
        },
        error => {
          console.warn('Could not fetch fileUrl for file:', file.id, error)
        }
      )
    }
  }
}
